/**
 * The cross-section view (spec §4d): the two compared surfaces drawn across
 * the corridor at the walked station, with the cut and fill bands between
 * them hatched.
 *
 * Two things make this a section and not a debug plot. First, it draws the
 * two *surfaces* — a Δz ribbon around a flat baseline is not something an
 * engineer recognises, and it also hides which surface moved. Second, the
 * vertical exaggeration is a real, stated scale: pixels per metre of Z are
 * exactly `ve ×` pixels per metre of offset (ruling T13.2), so the `5×` on
 * the label is the factor in the drawing rather than a caption on a
 * fit-to-pane stretch. An unlabelled or mislabelled exaggeration is how a
 * 40 mm dip gets read as a metre.
 *
 * Because `zTop` / `zBottom` come from the surfaces and not from the
 * centreline, a flat GeoJSON alignment still produces a correct section
 * (spec §4d, R5). Only the alignment's own profile is missing, and the
 * caption says so.
 *
 * The arithmetic lives in `@/lib/stationVolumes/sectionModel`; this file is
 * the drawing, the interaction and the copy.
 */
import { CUT_LEGEND_SHORT_KEY, FILL_LEGEND_SHORT_KEY } from '@/lib/cutFillConvention';
import { alignmentsAtom, alignmentWalkAtom } from '@/atoms/alignment.atom';
import { stationClipToBinAtom } from '@/atoms/stationVolumes.atom';
import { viewerEvents } from '@/atoms/viewer.atom';
import { SectionCrossfallLabels } from '@/components/StationSectionCrossfallLabels';
import { LoadingBadge } from '@/components/ui/LoadingBadge';
import { isFlat } from '@/core/alignment';
import {
  sectionLineAt,
  sectionRowAt,
  useSectionOverlays,
  useStationOffsets,
  useStationSections,
} from '@/hooks/useStationVolumes';
import { binCentreStation, binIndexForStation, clipDimsForRun } from '@/lib/stationVolumes/aggregate';
import { formatFixed, formatM2, formatPct, formatStationRange, staOnly } from '@/lib/stationVolumes/format';
import { lookAtSectionPose } from '@/lib/stationVolumes/sectionOverlay';
import {
  overlaysMatchRun,
  overlayStates,
  type OverlayState,
  type SectionOverlaySetting,
} from '@/lib/stationVolumes/sectionOverlays';
import { activePairIndices, overlaySeries, runSeries, seriesRowsAt } from '@/lib/stationVolumes/sectionSeries';
import {
  autoExaggeration,
  domainClipsSection,
  nearestOffsetIndex,
  nearestStationLine,
  noDataRunsAcross,
  offsetCentre,
  offsetTickLabel,
  pixelsPerMetreZ,
  sectionLayers,
  sectionSpansAcross,
  validRuns,
  VE_PRESETS,
  zDomain,
  type OffsetRun,
  type SectionLayerExpectation,
  type SectionPoint,
} from '@/lib/stationVolumes/sectionModel';
import type {
  SectionOverlaysLayerMeta,
  StationVolumesAnalysisKind,
  StationVolumesJson,
  StationVolumesMeta,
} from '@/lib/stationVolumes/types';
import { useSvPalette } from '@/lib/stationVolumes/useSvPalette';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { Group } from '@visx/group';
import { scaleLinear } from '@visx/scale';
import { useTooltip, useTooltipInPortal } from '@visx/tooltip';
import { useAtom, useAtomValue } from 'jotai';
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';

import {
  FLOATING_TOOLTIP_STYLE,
  HiddenDataTable,
  TICK_FONT,
  TooltipCard,
  tickLabelProps,
} from './StationVolumeChartParts';

export interface StationSectionChartProps {
  json: StationVolumesJson;
  meta: StationVolumesMeta;
  /**
   * The analysed layer. The section reads one piece of per-layer state of its
   * own — "Clip to station" (`stationClipToBinAtom`, spec §11.1) — to caption
   * what the clip box is doing while it is on.
   */
  layerId: string;
  /** Batch that owns the `station_offsets.f32` byproduct. */
  batchId?: string;
  /**
   * Which analysis produced the run. It decides the *copy*, not the drawing:
   * on a deviation run the two surfaces are the design model and the point
   * cloud, not "top" and "bottom" surfaces, and every sentence that names them
   * has to say so (spec §10.3). Defaults to the surface-difference wording,
   * which is what a run written before `kind` existed is.
   */
  kind?: StationVolumesAnalysisKind;
  /**
   * Names of the two compared surfaces. They live on the layer's
   * `metadata.surfaceDifference`, not in `station_volumes.json`, so the dock
   * — which holds the layer — passes them down. The drawing and legend never
   * print these verbatim (real file names read as noise on the chart, per
   * spec follow-up); the legend reads the generic ROLE name for the run's
   * `kind` ("Top surface" / "Bottom surface", or "Design model" / "Point
   * cloud"), and the only place a real name surfaces is a `title` tooltip on
   * the matching legend swatch, so the mapping stays discoverable on hover.
   */
  topLayerName?: string;
  bottomLayerName?: string;
  /**
   * The run's chosen expectation, if it had one (design doc §1, §8.2) — just
   * the four scalar inputs `toleranceRuns` needs, not either summary block's
   * full share/verdict figures. The two upstream shapes that carry these
   * (`ExpectationMeta` on a surface-difference run's `stats.expectation`,
   * `StationCorridorExpectation` on a deviation run's
   * `deviationStats.corridor.expectation`) agree on all four fields, so the
   * dock passes whichever one this run has straight through.
   *
   * `null`/absent means a `NONE`-mode run, exactly like every run made
   * before this feature existed: no overlay, no legend entry, nothing drawn
   * (additive and optional, per the design doc).
   */
  expectation?: SectionExpectationInput | null;
  /**
   * Which two planes of a multi-plane `station_sections.f32` bound the layer
   * on screen — the multi-layer stack's section (spec: multi-layer thickness
   * §6.2).
   *
   * A stack writes 2N planes, `asbuilt:<level>` for every level in level
   * order then `design:<level>`, so LAYER `i` is bounded by planes `i` and
   * `i + 1`. `decodeStationSections` names `zTop`/`zBottom` after planes 0
   * and 1 — right for layer 0 and wrong for every other one — so the chip
   * strip passes the pair it wants and the two accessors are re-pointed
   * below.
   *
   * Absent on every single-pair run, which is the 2-plane case where planes 0
   * and 1 already ARE the pair.
   */
  planePair?: [string, string] | null;
  /**
   * A stack's per-LEVEL tolerances, in level order — exactly
   * `metadata.layerStack.levels[]` (`postprocess.ts`), which is exactly
   * `stack_analysis.py`'s `levels[]`.
   *
   * Why this and not `expectation`: a stack has no single run-wide
   * expectation. Every layer is judged against its OWN design thickness (the
   * design planes in the section file) and its OWN tolerances, and the worker
   * judges layer `i` against LEVEL `i`'s pair — the TOP of the two. The
   * section has to classify the same way the verdict did, or the drawing and
   * the compliance chip contradict each other on one screen.
   *
   * Absent on every single-pair run, and on a stack made before the
   * tolerances were echoed onto the result document: no marking at all, never
   * a zero tolerance, which would mark every column out of spec.
   */
  levelTolerances?: readonly (SectionLevelTolerance | null)[] | null;
  /**
   * `metadata.sectionOverlays` — where the sampled REFERENCE planes live and,
   * crucially, which settings list they were sampled FOR (section-overlays
   * spec §3). Absent/null on every layer with no overlay configured, which is
   * every layer made before the feature existed: nothing fetched, nothing
   * drawn, no legend entry, no badge.
   */
  overlaysMeta?: SectionOverlaysLayerMeta | null;
  /**
   * What the LAYER is configured with today (`settings.sectionOverlays`), in
   * the user's own order — the list the legend enumerates and the inks are
   * assigned from.
   *
   * It is deliberately separate from `overlaysMeta`: the two disagree for as
   * long as it takes the server to re-sample, and that disagreement is exactly
   * what the "pending" state is. Reading the configured list out of the
   * byproduct instead would make a stale file look authoritative.
   */
  overlaySettings?: readonly SectionOverlaySetting[];
  /**
   * Display names by overlay id, derived by the DOCK —
   * `<layer name> · <surface/plane/method>` — because only it holds the layer
   * registry the ids point into, and only it has a translator.
   *
   * These WIN over the file's own label. The server writes the same shape but
   * in English at sample time, so preferring the file renamed a legend row
   * the moment the sample landed. The file answers only for an overlay the
   * dock cannot name at all (a deleted source layer).
   */
  overlayLabels?: Readonly<Record<string, string>>;
}

/** Module-level so the default is one stable identity, not a fresh `[]` per
 *  render feeding every downstream `useMemo` a changed dependency. */
const NO_OVERLAYS: readonly SectionOverlaySetting[] = [];

/** One LEVEL's tolerances. See `StationSectionChartProps.levelTolerances`. */
export interface SectionLevelTolerance {
  underToleranceM: number | null;
  overToleranceM: number | null;
}

/** See `StationSectionChartProps.expectation`. */
export interface SectionExpectationInput {
  mode: 'MATCH' | 'CONSTANT' | 'SURFACES';
  /** `null` for SURFACES (no single scalar) and for a run with no station
   *  volumes when the mode is otherwise; `0` for MATCH; a real design value
   *  for CONSTANT. */
  thicknessM: number | null;
  underToleranceM: number | null;
  overToleranceM: number | null;
}

/** Plot margins: `left` carries elevation ticks, `bottom` the offset axis. */
const MARGIN = { top: 14, right: 16, bottom: 34, left: 58 } as const;
const MIN_PLOT_W = 240;
/**
 * Absolute floor for the plot, NOT a target: it only keeps the SVG off zero
 * while the dock is dragged to its 180 px minimum. The MEASURED body height
 * always wins above it — a floor taller than the body is what pushed the
 * legend row out under the dock footer at the default 280 px height, and the
 * two panes share whatever is left (`chartLayout` splits 2:1, so the mass-haul
 * pane is the one that gives first).
 */
const MIN_PLOT_H = 40;
/** Used until the ResizeObserver reports — the dock's default body box. */
const DEFAULT_W = 760;
const DEFAULT_H = 220;
/** At most this many rows in the visually-hidden table; wide swaths sample. */
const SR_MAX_ROWS = 41;
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

/**
 * Section-local CSS: the rules that need this chart's own focus affordance
 * and its behaviour under forced colours. The palette itself is `SV_TOKENS`
 * on the dock root.
 */
const SECTION_CSS = `
.sv-section:focus-visible{outline:2px solid var(--sv-walk);outline-offset:2px;border-radius:4px;}
.sv-section-crosshair{pointer-events:none;}
/* Forced colours: the washes collapse to Canvas, so the hatch on top of them
   is the only thing separating cut from fill. Keep it, and keep the two
   surfaces distinguishable by dash pattern rather than by ink. */
@media (forced-colors:active){
  .sv-section-wash{fill:Canvas;}
  .sv-section-surface{stroke:CanvasText;}
  /* A reference's only identity is its INK, and forced colours take every ink
     away — every line on the drawing becomes CanvasText. So each reference
     falls back to a dash pattern instead, which is the one channel forced
     colours leaves alone. None of the eight is the design lines' \`2,3\` or the
     legacy pair's \`5,3\`, and no two are the same. */
  .sv-section-ref-0{stroke-dasharray:6 2;}
  .sv-section-ref-1{stroke-dasharray:1 2;}
  .sv-section-ref-2{stroke-dasharray:6 2 1 2;}
  .sv-section-ref-3{stroke-dasharray:3 2;}
  .sv-section-ref-4{stroke-dasharray:8 3;}
  .sv-section-ref-5{stroke-dasharray:2 4;}
  .sv-section-ref-6{stroke-dasharray:6 2 1 2 1 2;}
  .sv-section-ref-7{stroke-dasharray:4 1;}
}
`;

/** One polyline per measured run, so a gap is a gap and not a bridge. */
function polylinePath(
  values: Float32Array,
  runs: OffsetRun[],
  offsetStartM: number,
  offsetStepM: number,
  x: (o: number) => number,
  y: (z: number) => number,
): string {
  let d = '';
  for (const run of runs) {
    for (let j = run.from; j <= run.to; j++) {
      const px = x(offsetCentre(offsetStartM, offsetStepM, j)).toFixed(2);
      const py = y(values[j]).toFixed(2);
      d += `${j === run.from ? 'M' : 'L'}${px},${py}`;
    }
  }
  return d;
}

/**
 * A length in metres for prose: whole numbers stay whole (`20 m`, not
 * `20.00 m`), a fractional one keeps two decimals. Goes through `format.ts`
 * like every other figure in this feature (ruling D8).
 */
const metres = (v: number) => formatFixed(v, Number.isInteger(v) ? 0 : 2);

/**
 * A REFERENCE's Δ to the surface, signed.
 *
 * `formatFixed` already prints the house minus for a negative, but a positive
 * difference has to say so out loud: "0.03" and "+0.03" answer different
 * questions when the reader is asking which side of the as-built the
 * reference sits on. A Δ that rounds to zero keeps no sign, exactly as every
 * other figure in this feature does.
 */
const signedDelta = (v: number) => {
  const s = formatFixed(v, 2);
  // Sign the ROUNDED figure, not the raw one: `+0.004` rounds to `0.00`, and
  // `+0.00` claims a direction the printed number does not show. `formatFixed`
  // already drops the sign from a negative that rounds to zero for exactly the
  // same reason, so this keeps the two ends symmetric.
  return v > 0 && /[1-9]/.test(s) ? `+${s}` : s;
};

const polygonPoints = (pts: SectionPoint[], x: (o: number) => number, y: (z: number) => number): string =>
  pts.map((p) => `${x(p.o).toFixed(2)},${y(p.z).toFixed(2)}`).join(' ');

/**
 * The per-LAYER hatches of a stacked section (multi-layer thickness §6.2).
 *
 * In a two-surface comparison the hatch says cut or fill — a sign. In a stack
 * every layer is material and the sign is almost always the same, so the hatch
 * has to say WHICH LAYER instead: a reader looking at 40 mm of wearing course
 * over 60 mm of binder must be able to tell the two apart on the drawing, and
 * on a printed one.
 *
 * Every motif is drawn in ONE ink (`currentColor`) at one weight, so colour
 * carries no information at all and a greyscale print loses nothing — identity
 * is the GEOMETRY. None of them repeats a motif already spoken for: 45°/135°
 * are the cut/fill hatches, which a stack does not draw, while the vertical
 * no-data stripes and the three tolerance motifs (horizontal, diagonal grid,
 * dots) are all still live over a stack and are deliberately avoided here.
 *
 * EIGHT motifs, because there were four and `i % 4` meant a six-level stack —
 * five layers — drew layers 0 and 4 identically, which is precisely the
 * collision the hatch exists to prevent. Eight covers nine levels, past any
 * real pavement; the list still cycles beyond that, where two layers eight
 * apart sharing a hatch is a far smaller problem than two ADJACENT layers
 * sharing one, which cannot happen.
 *
 * The set was chosen by RASTERISING each tile through `rsvg-convert` at 16
 * samples/unit and measuring it, not by eye — at 8 px a motif's weight is not
 * something you can judge from the source:
 *
 *   * INK COVERAGE 20.6–21.5 % across all eight. A heavier motif reads as a
 *     darker band, and a darker band on a compliance drawing reads as
 *     SEVERITY. That is what rules out the obvious fillers — a full
 *     checkerboard is 50 % and looks like a verdict — and it is why the
 *     staggered squares below are 2.6 units and not the 3 they shipped at
 *     (3 measured 28.1 %, the one outlier of the original four).
 *   * SHAPE SEPARATION, as the largest overlap between any two tiles over
 *     every cyclic shift of one against the other — shifts included because a
 *     hatch translated by one pitch is the same hatch. Worst pair 0.45,
 *     worst ADJACENT pair 0.42. Rejected on this measure: a rounded chevron
 *     (0.72 against the chevron), a dashed 45° (0.55 against the solid 45°),
 *     and every second centred outline — a square outline or a diamond scores
 *     0.66/0.71 against the ring.
 */
const LAYER_HATCH_TILE = 8;
const LAYER_HATCHES: ((ink: string) => ReactNode)[] = [
  // 45°, single direction. The overhanging strokes make the tile seamless.
  (ink) => <path d='M0,8 L8,0 M-2,2 L2,-2 M6,10 L10,6' stroke={ink} strokeWidth={1.2} fill='none' />,
  // 135°, single direction.
  (ink) => <path d='M0,0 L8,8 M-2,6 L2,10 M6,-2 L10,2' stroke={ink} strokeWidth={1.2} fill='none' />,
  // Chevrons — a zigzag reads as neither of the two diagonals. ONE V lands
  // inside the tile, not three: a tighter zigzag doubles its own ink and the
  // layer starts reading as darker, which is severity, not identity. The two
  // outer V's fall wholly outside the 8 × 8 tile and are clipped away — unlike
  // the corner stubs of the two diagonals above, which really do land inside
  // it, these buy no seam and are kept only so the three subpaths read as one
  // continuous zigzag in the source.
  (ink) => <path d='M0,6 L4,2 L8,6 M0,14 L4,10 L8,14 M0,-2 L4,-6 L8,-2' stroke={ink} strokeWidth={1.2} fill='none' />,
  // Staggered squares: solid marks, no strokes at all — the one motif here
  // that is not a line. Sized so its ink coverage matches the hatches rather
  // than reading as a heavier, more alarming layer.
  (ink) => (
    <>
      <rect x={0.7} y={0.7} width={2.6} height={2.6} fill={ink} />
      <rect x={4.7} y={4.7} width={2.6} height={2.6} fill={ink} />
    </>
  ),
  // The chevron turned a quarter: a zigzag running DOWN the band rather than
  // across it. Same ink, same construction, and it cannot be confused with the
  // horizontal one — a hatch is read as a direction first.
  (ink) => <path d='M6,0 L2,4 L6,8 M14,0 L10,4 L14,8 M-2,0 L-6,4 L-2,8' stroke={ink} strokeWidth={1.2} fill='none' />,
  // Rings: the only CURVED motif on the drawing, which is why it survives
  // beside seven straight-edged ones. Open, not solid — a filled circle is the
  // `crossing` tolerance overlay.
  (ink) => <circle cx={4} cy={4} r={1.8} fill='none' stroke={ink} strokeWidth={1.2} />,
  // One solid triangle per tile. Solid like the squares, but on a different
  // lattice — one mark per tile against their two — which is what keeps the
  // two apart: two solid marks staggered the same way score 0.66 however
  // differently they are shaped.
  (ink) => <path d='M1,6.2 L7,6.2 L4,1.7 Z' fill={ink} />,
  // Corner brackets. Orthogonal, but broken at both ends, so it reads as a
  // bracket rather than as the lattice the `over` overlay draws.
  (ink) => <path d='M1.2,1.2 L1.2,6.8 L6.8,6.8' stroke={ink} strokeWidth={1.2} fill='none' />,
];
const layerHatch = (i: number) => LAYER_HATCHES[i % LAYER_HATCHES.length];

export const StationSectionChart = ({
  json,
  meta,
  layerId,
  batchId,
  kind = 'surfaceDifference',
  topLayerName,
  bottomLayerName,
  expectation,
  planePair = null,
  levelTolerances = null,
  overlaysMeta = null,
  overlaySettings = NO_OVERLAYS,
  overlayLabels,
}: StationSectionChartProps) => {
  const { t } = useTranslation('project');
  /** Copy switch only — nothing below it changes what is drawn. */
  const deviation = kind === 'deviation';
  const [walk, setWalk] = useAtom(alignmentWalkAtom);
  const clipToBin = useAtomValue(stationClipToBinAtom(layerId));
  const alignments = useAtomValue(alignmentsAtom);
  const [veMode, setVeMode] = useState<'auto' | number>('auto');
  const [hoverJ, setHoverJ] = useState<number | null>(null);
  const [box, setBox] = useState<{ w: number; h: number } | null>(null);
  const plotRef = useRef<HTMLDivElement | null>(null);
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  // `zIndex` is explicit rather than left to the Portal default: the dock
  // body is `overflow-hidden` inside a flex layout, and without a high stated
  // z-index the portalled tooltip can still end up under floating-window
  // siblings or SVG layers that establish their own stacking context.
  const { containerRef, TooltipInPortal } = useTooltipInPortal({ scroll: true, detectBounds: true, zIndex: 9999 });
  const { tooltipData, tooltipLeft, tooltipTop, tooltipOpen, showTooltip, hideTooltip } = useTooltip<{ j: number }>();

  const hasOffsetsByproduct = !!meta.byproducts.offsets && !!meta.offsetBins;
  const { data: offsets, isLoading, error, progress, refetch } = useStationOffsets(meta, batchId, hasOffsetsByproduct);
  // The TRUE sections cut at the station lines. Absent on a run made before
  // they existed, in which case everything below falls back to the bin-mean
  // columns — today's behaviour unchanged, not a degraded variant.
  const hasSectionsByproduct = !!meta.byproducts?.sections && !!meta.sections;
  const { data: decodedPlanes } = useStationSections(meta, batchId, hasSectionsByproduct);
  /**
   * Re-point `zTop`/`zBottom` at the pair the caller asked for. Views into
   * the already-decoded buffer — no copy, so switching between the chips of a
   * stack costs nothing and never refetches. A pair naming a plane the file
   * does not have falls back to the decoder's own first two rather than
   * drawing a blank section with no explanation.
   */
  const sectionPlanes = useMemo(() => {
    if (!decodedPlanes || !planePair) return decodedPlanes;
    const zTop = decodedPlanes.planes[planePair[0]];
    const zBottom = decodedPlanes.planes[planePair[1]];
    return zTop && zBottom ? { ...decodedPlanes, zTop, zBottom } : decodedPlanes;
  }, [decodedPlanes, planePair]);

  // Measure the plot box: the section is drawn into whatever the dock's
  // resize grip left over, and the vertical exaggeration is computed from it.
  useEffect(() => {
    const el = plotRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      if (!r || r.width <= 0 || r.height <= 0) return;
      setBox((prev) => (prev && prev.w === r.width && prev.h === r.height ? prev : { w: r.width, h: r.height }));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const width = Math.max(MIN_PLOT_W, box?.w ?? DEFAULT_W);
  const height = Math.max(MIN_PLOT_H, box?.h ?? DEFAULT_H);
  const plotW = Math.max(1, width - MARGIN.left - MARGIN.right);
  const plotH = Math.max(1, height - MARGIN.top - MARGIN.bottom);

  // ------------------------------------------------------------ which bin
  const nBins = json.bins.length;
  const walkActive = walk?.key === json.alignment.key;
  const station = walkActive ? walk.station : json.params.stationFromInternalM;
  const binIndex = binIndexForStation(json.params, nBins, station);
  const bin = json.bins[binIndex];

  // A section is cut at a PLANE, so with true station-line sections the one on
  // screen is the nearest line to the walked station — not the bin the station
  // happens to fall inside. That also makes the final end station reachable:
  // it is a station line, and there is no bin past it.
  const stationLines = json.stations ?? null;
  const trueSections = !!stationLines && stationLines.length > 0 && !!sectionPlanes;
  const lineIndex = useMemo(
    () => (stationLines ? nearestStationLine(stationLines, station) : 0),
    [stationLines, station],
  );
  const line = trueSections ? stationLines[lineIndex] : null;

  // No copy and no interpolation between stations: blending two sections would
  // invent geometry. `dz` is materialised for the true-section branch because
  // there it is exactly `zTop − zBottom` with no averaging — the worker does
  // not store a third plane for what a subtraction gives.
  const row = useMemo(() => {
    if (trueSections && sectionPlanes) {
      const r = sectionLineAt(sectionPlanes, lineIndex);
      const dz = new Float32Array(r.zTop.length);
      for (let j = 0; j < dz.length; j++) dz[j] = r.zTop[j] - r.zBottom[j];
      return { zTop: r.zTop, zBottom: r.zBottom, dz };
    }
    return offsets ? sectionRowAt(offsets, binIndex) : null;
  }, [trueSections, sectionPlanes, lineIndex, offsets, binIndex]);

  const axisSource = trueSections ? sectionPlanes : offsets;
  const offsetStartM = axisSource?.offsetStartM ?? -json.params.swathLeftM;
  const offsetStepM = axisSource?.offsetStepM ?? json.params.offsetBinM;
  const nOffsets = axisSource?.nOffsets ?? 0;
  const offsetEndM = nOffsets > 0 ? offsetStartM + nOffsets * offsetStepM : json.params.swathRightM;
  const offsetSpan = Math.max(offsetEndM - offsetStartM, 1e-6);

  // --------------------------------------------------------- overlays
  /**
   * The REFERENCE overlays (section-overlays spec §3): surfaces from OTHER
   * layers, sampled server-side onto this run's own offset grid.
   *
   * A plane is drawn when its ID IS IN THE FILE and the file's grid is this
   * run's grid (spec §7.4). Nothing else gates it — in particular NOT the
   * settings hash: an id encodes the source layer, the surface and the
   * reduction, and the file is sampled on the run's own columns, so a plane
   * the file carries is the same line whatever OTHER overlays have been added
   * since. Gating on the hash blanked every drawn reference for the length of
   * a re-sample the moment a ninth overlay was configured.
   *
   * What a stale file means is therefore exactly: the ids it does not carry
   * are `pending`. Everything else the reader is told comes from `states`,
   * which separates "not sampled yet" from "the server tried and could not".
   *
   * An overlays file whose `lineSource` is `BIN_CENTRES` is indexed by bin and
   * one whose is `STATIONS` by station line — the same `rowIndex` the run
   * uses, because the preprocess chose the mode from the RUN's own file. That
   * choice is a claim about the run AS IT WAS SAMPLED, and a re-analysis at a
   * different resolution or swath moves the grid under it, so it is CHECKED
   * (`overlaysMatchRun`) rather than trusted.
   */
  const palette = useSvPalette();
  const { data: overlays, error: overlaysError } = useSectionOverlays(overlaysMeta, batchId, !!overlaysMeta);
  /**
   * The run's own grid, as the rows on screen are actually addressed. `null`
   * until a byproduct is decoded — with no run rows there is no section to
   * draw an overlay on, so there is nothing to disagree with either.
   */
  const runGrid = useMemo(
    () =>
      trueSections && sectionPlanes
        ? { trueSections: true, nStationLines: sectionPlanes.nStationLines, nOffsets: sectionPlanes.nOffsets }
        : offsets
          ? { trueSections: false, nStationLines: offsets.nStations, nOffsets: offsets.nOffsets }
          : null,
    [trueSections, sectionPlanes, offsets],
  );
  /** A file sampled on a grid that is no longer this run's. Decodes cleanly
   *  and would draw every reference at the wrong offset or the wrong
   *  chainage — the one failure mode that looks correct. */
  const overlaysGridMismatch = !!overlays && !!runGrid && !overlaysMatchRun(overlays.meta, runGrid);
  /**
   * A fetch or decode failure is a FAILURE, not slow progress. Without this,
   * a 403 on the signed URL left `overlays` null for ever, every overlay
   * "pending", and a spinner claiming the server was still working — a
   * permanent lie. One dead file kills every overlay on it, so they all take
   * the same message — and a grid mismatch is the same shape of verdict: it
   * condemns the FILE, not one plane in it.
   */
  const overlayState = useMemo(() => {
    const failAll = (error: string) => {
      const failed: Record<string, { state: OverlayState; error?: string | null }> = {};
      for (const s of overlaySettings) failed[s.id] = { state: 'failed', error };
      return failed;
    };
    if (overlaysError) return failAll(overlaysError.message);
    if (overlaysGridMismatch) return failAll(t('stationVolumes.section.overlays.gridMismatch'));
    return overlayStates(overlaySettings, overlays);
  }, [overlaySettings, overlays, overlaysError, overlaysGridMismatch, t]);
  /** Whether the FILE may be drawn from at all. Which of its planes are drawn
   *  is `overlaySeries`' own id match. */
  const overlaysUsable = !overlaysError && !overlaysGridMismatch && !!overlays;
  /** The two file-wide verdicts, for the badge: both condemn the whole file
   *  and neither is progress. `overlaysLoading` is deliberately not in here —
   *  a fetch in flight is what the PENDING half of the badge is for. */
  const overlaysFileFailed = !!overlaysError || overlaysGridMismatch;

  /**
   * The name a reader sees for one overlay, in three steps: the DOCK's
   * derived name → the file's own label → a positional "Overlay 2".
   *
   * The dock comes FIRST, which inverts the order this shipped with. The two
   * strings are the same shape (`<layer> · <surface/plane/method>`) built
   * from the same two names, but the dock's is localised and the file's is
   * whatever English the server wrote at sample time — so preferring the file
   * made a row RENAME ITSELF the moment the sample landed, in a non-English
   * UI from a translated name to an English one. A legend entry that changes
   * its wording under the reader is worse than one that arrives late.
   *
   * The file's label still answers for an overlay the dock cannot name (its
   * source layer has left the project), which is the case the dock returns
   * a positional name for.
   *
   * Never an id, and never a status sentence: a row headed "Sampling overlays
   * on the server" is a progress message wearing a name's clothes, and the
   * reader cannot tell which of their three configured overlays it is.
   */
  const overlayNames = useMemo(() => {
    const out: Record<string, string> = {};
    overlaySettings.forEach((s, i) => {
      out[s.id] =
        overlayLabels?.[s.id]?.trim() ||
        overlays?.meta.planes.find((p) => p.id === s.id)?.label?.trim() ||
        t('stationVolumes.section.overlays.unnamed', { n: i + 1 });
    });
    return out;
  }, [overlaySettings, overlays, overlayLabels, t]);

  /**
   * One legend row per CONFIGURED, non-hidden overlay, in the user's order —
   * including the ones with no line on the drawing, which is the only place
   * their state is explained. Hidden ones are absent entirely: the user asked
   * for them not to be on this chart.
   *
   * The ink index is the setting's place in the FULL list so it matches
   * `overlaySeries`, and the visible name is always the FILE's `plane.label`
   * (`<layer> · <surface>`, derived server-side) — never the id, which is a
   * key and reads as noise. A pending overlay keeps the name the last sample
   * gave it, so a re-sample mutes the row rather than blanking it.
   */
  const overlayLegend = useMemo(
    () =>
      overlaySettings
        .map((s, i) => ({
          id: s.id,
          hidden: s.hidden,
          state: overlayState[s.id]?.state ?? 'pending',
          error: overlayState[s.id]?.error ?? null,
          label: overlayNames[s.id],
          color: palette.series[i % palette.series.length],
        }))
        .filter((e) => !e.hidden),
    [overlaySettings, overlayState, overlayNames, palette.series],
  );
  /** Only a PENDING overlay earns the badge: a failure is not in progress. */
  const overlaysPending = overlayLegend.some((e) => e.state === 'pending');

  // ------------------------------------------------------------ series
  /**
   * Every drawn plane as one list (section-overlays spec §3). The run's own
   * file gives the as-built and design series; the bin-mean planes stand in
   * when there are no true sections. Reference series (overlays) join this
   * list in Phase 3 — they extend the span and the legend, never the bands.
   *
   * `runSeries` carries the stack's own partition: a file naming its planes
   * `asbuilt:<level>` … `design:<level>` splits by prefix, while every run
   * made before the stack named exactly two, `zTop` and `zBottom`, and those
   * files are still on S3 and still opened daily — which is why the as-built
   * side falls back to "all of them" rather than filtering (R14).
   */
  const series = useMemo(
    () => [
      ...runSeries(trueSections ? sectionPlanes : null, trueSections ? null : offsets),
      ...(overlaysUsable ? overlaySeries(overlays, overlaySettings, palette.series) : []),
    ],
    [trueSections, sectionPlanes, offsets, overlaysUsable, overlays, overlaySettings, palette.series],
  );

  /**
   * One station line's columns for every drawn plane, partitioned by role.
   * Views into the decoded buffer, never copies — switching station lines
   * allocates nothing. `k` is a station-line index for true sections and a
   * BIN index for the bin-mean fallback, exactly as the chart has always
   * addressed its rows.
   */
  const rowIndex = trueSections ? lineIndex : binIndex;
  const seriesRows = useMemo(() => seriesRowsAt(series, rowIndex), [series, rowIndex]);
  const levelRows = seriesRows.asbuilt;
  const designRows = seriesRows.design;
  const referenceRows = seriesRows.reference;
  /** The level names the legend prints, prefix stripped. */
  const levelLabels = seriesRows.asbuiltLabels;

  /**
   * Which of the drawn levels the DOCK selected (`planePair`). It decides what
   * the readout, the tooltip and the hidden table describe, and which pair of
   * lines is drawn at full weight — the rest of the stack is context.
   */
  const activePair = useMemo(() => activePairIndices(seriesRows, planePair), [seriesRows, planePair]);

  // ------------------------------------------------------------ geometry
  /** A reference extends the drawn Z range — it is a line on the same sheet. */
  const spans = useMemo(
    () => sectionSpansAcross([...levelRows, ...designRows, ...referenceRows.map((r) => r.row)]),
    [levelRows, designRows, referenceRows],
  );
  const autoVe = useMemo(
    () => autoExaggeration(spans.hi - spans.lo, offsetSpan, plotW, plotH),
    [spans.hi, spans.lo, offsetSpan, plotW, plotH],
  );
  const ve = veMode === 'auto' ? autoVe : veMode;

  const xScale = useMemo(
    () => scaleLinear<number>({ domain: [offsetStartM, offsetEndM], range: [0, plotW] }),
    [offsetStartM, offsetEndM, plotW],
  );
  const zRange = useMemo(() => zDomain(spans, ve, offsetSpan, plotW, plotH), [spans, ve, offsetSpan, plotW, plotH]);
  const yScale = useMemo(() => scaleLinear<number>({ domain: zRange, range: [plotH, 0] }), [zRange, plotH]);

  const gaps = useMemo(() => noDataRunsAcross(levelRows), [levelRows]);

  /**
   * How the run's expectation reaches each layer.
   *
   * `MATCH` supplies zero and `CONSTANT` a scalar. `SURFACES` has no scalar —
   * its expectation is itself a second set of surfaces. Until the stack, that
   * per-column raster never reached the client and the section drew nothing
   * (surface-expectations §8.2a); a stack file carries the design planes, so
   * each layer is now compared against `design[i] − design[i+1]` COLUMN BY
   * COLUMN. A SURFACES run whose file has no design planes — every
   * surface-difference run made before the stack — still draws no overlay, and
   * still says so in the legend.
   */
  const layerExpectation = useMemo(
    () =>
      expectation
        ? {
            expectedM:
              expectation.mode === 'MATCH' ? 0 : expectation.mode === 'CONSTANT' ? expectation.thicknessM : null,
            fromDesignPlanes: expectation.mode === 'SURFACES',
            underToleranceM: expectation.underToleranceM ?? null,
            overToleranceM: expectation.overToleranceM ?? null,
          }
        : null,
    [expectation],
  );

  /**
   * A STACK's expectation, one entry per LAYER.
   *
   * A stack has no run-wide expectation to pass down — `metadata.layerStack`
   * carries neither `deviationStats.corridor.expectation` nor
   * `surfaceDifference.stats.expectation`, which is why `expectation` above is
   * always `null` for one. What it does carry is each LEVEL's tolerances, and
   * the design thickness is already on screen as the design planes. Layer `i`
   * takes LEVEL `i`'s tolerances — the TOP of its pair — because that is the
   * pair the worker judged it with (`stack_analysis.py`: `_judge(actual,
   * design, levels[a].underTolM, levels[a].overTolM, 'SURFACES')`). Classify
   * any other way and the section disagrees with the chip beside it.
   *
   * `null` — and so no marking at all — unless the file actually carries
   * design planes to compare each column against AND at least one level
   * states a tolerance. A missing tolerance is never read as zero: zero marks
   * every column out of spec.
   */
  const stackExpectations = useMemo<(SectionLayerExpectation | null)[] | null>(() => {
    const nLayers = Math.max(levelRows.length - 1, 0);
    if (!levelTolerances || nLayers === 0 || designRows.length === 0) return null;
    const per = Array.from({ length: nLayers }, (_, i) => {
      const tol = levelTolerances[i] ?? null;
      if (!tol || (tol.underToleranceM == null && tol.overToleranceM == null)) return null;
      return {
        // The expected thickness is the gap between this layer's two DESIGN
        // planes, per column — never a scalar (§2.2: a constant would flag the
        // design's own crossfall as non-compliance).
        expectedM: null,
        fromDesignPlanes: true,
        underToleranceM: tol.underToleranceM,
        overToleranceM: tol.overToleranceM,
      } satisfies SectionLayerExpectation;
    });
    return per.some((e) => e !== null) ? per : null;
  }, [levelTolerances, levelRows.length, designRows.length]);

  /** The N−1 layers, each with its OWN band and its OWN tolerance verdict. */
  const layers = useMemo(
    () => sectionLayers(levelRows, designRows, offsetStartM, offsetStepM, stackExpectations ?? layerExpectation),
    [levelRows, designRows, offsetStartM, offsetStepM, stackExpectations, layerExpectation],
  );
  /**
   * Is this the STACK drawing? It is a different drawing from a two-surface
   * comparison, not a variant of it: there the hatch means cut or fill, here
   * it means which layer, and the two must not be mixed on one pane.
   * Everything below that branches on `isStack` branches for that reason, and
   * the single-pair side is the shipped drawing, unchanged.
   *
   * DESIGN PLANES, not the layer count, are what identify a stack. A stack of
   * two levels is legal (`layer_stack.py` requires two, i.e. ONE layer — a
   * wearing course over an existing surface) and it writes its design planes
   * like any other; gating on `layers.length > 1` drew that run's dashed
   * design lines beside a Cut/Fill key describing a comparison it is not
   * making, with nothing in the legend naming the dashes. Only a stack file
   * carries `design:` planes — a legacy two-plane file has none
   * (`designPlaneIndices` deliberately has no legacy fallback), so this cannot
   * pull a pre-stack run into the stack drawing.
   */
  const isStack = layers.length > 1 || designRows.length > 0;
  /** Full-height overlays are only legible while there is ONE band. */
  const outRuns = isStack ? [] : (layers[0]?.out ?? []);
  /** Every kind marked anywhere on the section — what the legend explains. */
  const outKinds = useMemo(() => new Set(layers.flatMap((l) => l.out.map((r) => r.kind))), [layers]);
  const surfacesWithoutDesign = expectation?.mode === 'SURFACES' && designRows.length === 0;

  // The legend and every other piece of chart text always read the generic
  // role name — the real file names (`topLayerName` / `bottomLayerName`) are
  // long and inconsistent between survey and design layers, and printing them
  // on the drawing or in the legend is what made the chart unreadable. The
  // props are still accepted (the dock passes them) and surface only as a
  // `title` tooltip on the matching legend swatch below.
  //
  // "Top surface" / "Bottom surface" is the surface-difference vocabulary. A
  // deviation run always has the DESIGN on top and the point cloud underneath
  // (the worker feeds the reference in as `top`, so `dz = −deviation`), so it
  // names them: an engineer reading "Top surface" over a design model has to
  // guess which of the two moved.
  const topName = t(deviation ? 'stationVolumes.section.topFallbackDeviation' : 'stationVolumes.section.topFallback');
  const bottomName = t(
    deviation ? 'stationVolumes.section.bottomFallbackDeviation' : 'stationVolumes.section.bottomFallback',
  );

  /**
   * True when the chosen exaggeration pushes the section past the pane.
   *
   * `Auto` no longer reaches this: the ladder runs down to 0.2× and
   * `autoExaggeration` refuses any stop that would clip. What is left is the
   * reader's own choice of a deep stop, which is legitimate — and still has to
   * be said out loud, because the drawing gives no other sign that the top of
   * the structure is missing.
   */
  const clipped = domainClipsSection(spans, zRange);

  // ------------------------------------------------------------ walking
  const walkTo = useCallback(
    (next: number) => {
      // `follow` is left untouched: the camera moves only if the user asked
      // it to on the walk toolbar.
      setWalk((w) => (w && w.key === json.alignment.key ? { ...w, station: next } : w));
    },
    [json.alignment.key, setWalk],
  );

  const walkToBin = useCallback(
    (i: number) => {
      const target = json.bins[clamp(i, 0, nBins - 1)];
      if (!target) return;
      walkTo(binCentreStation(target));
    },
    [json.bins, nBins, walkTo],
  );

  /**
   * Step by station LINE when the run has them, by bin otherwise.
   *
   * Stepping by bin over true sections would skip the last end station, which
   * is a section like any other and the one a QS most often wants — the end of
   * the job.
   */
  const stepSection = useCallback(
    (dir: 1 | -1, big: boolean) => {
      const jump = big ? 10 : 1;
      if (trueSections && stationLines) {
        const k = clamp(lineIndex + dir * jump, 0, stationLines.length - 1);
        walkTo(stationLines[k].staInternalM);
        return;
      }
      walkToBin(binIndex + dir * jump);
    },
    [trueSections, stationLines, lineIndex, binIndex, walkTo, walkToBin],
  );

  /** The nearest bin either side that has cells — the way out of an empty one. */
  const jumpToData = useCallback(() => {
    for (let d = 1; d < nBins; d++) {
      for (const i of [binIndex - d, binIndex + d]) {
        if (json.bins[i]?.cellCount) {
          walkToBin(i);
          return;
        }
      }
    }
  }, [binIndex, json.bins, nBins, walkToBin]);

  /**
   * "Look at section" (spec §11.3): move the camera perpendicular to the
   * alignment at the walked station, framed on this run's own swath/height.
   * Requires a live walk on this alignment (a station to stand at) and the
   * registered alignment itself (a tangent to stand perpendicular to) — a
   * `station_volumes.json` alone carries neither.
   */
  const lookAtSection = useCallback(() => {
    const liveAlignment = alignments.get(json.alignment.key);
    if (!walkActive || !liveAlignment || !bin) return;
    const dims = clipDimsForRun(json.params, json.bins, offsets ?? null);
    viewerEvents.emit('lookAt', lookAtSectionPose(liveAlignment, binCentreStation(bin), dims.width, dims.height));
    // Camera follow on, so stepping to the next station keeps the section in
    // front of the user instead of leaving them looking at where it was.
    setWalk((w) => (w && w.follow ? w : w ? { ...w, follow: true } : w));
  }, [alignments, walkActive, bin, json, offsets, setWalk]);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (nBins === 0) return;
    if (e.key === 'l' || e.key === 'L') {
      // Defensive: this pane has no text inputs today, but never hijack a
      // letter key from one if it ever does.
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      e.preventDefault();
      e.stopPropagation();
      lookAtSection();
      return;
    }
    if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault();
      e.stopPropagation();
      if (trueSections && stationLines)
        walkTo(stationLines[e.key === 'Home' ? 0 : stationLines.length - 1].staInternalM);
      else walkToBin(e.key === 'Home' ? 0 : nBins - 1);
      return;
    }
    const dir = e.key === 'ArrowLeft' ? -1 : e.key === 'ArrowRight' ? 1 : 0;
    if (dir === 0) return;
    e.preventDefault();
    e.stopPropagation();
    stepSection(dir, e.shiftKey);
  };

  // ------------------------------------------------------------ hover
  const setPlotRef = useCallback(
    (el: HTMLDivElement | null) => {
      plotRef.current = el;
      containerRef(el);
    },
    [containerRef],
  );

  const onOverlayMove = (e: PointerEvent<SVGRectElement>) => {
    if (!offsets || nOffsets === 0) return;
    // `currentTarget` is the overlay rect itself, so this is the pointer's
    // position within the *plot* (no `MARGIN` offset yet) regardless of
    // where the rect sits on the page — correct under scroll, and under the
    // dock's `overflow-hidden` ancestor, since `getBoundingClientRect`
    // reports the element's real viewport position, not a clipped one.
    const x = e.clientX - e.currentTarget.getBoundingClientRect().left;
    const j = nearestOffsetIndex(xScale.invert(x), offsetStartM, offsetStepM, nOffsets);
    if (j === hoverJ) return;
    setHoverJ(j);
    showTooltip({
      tooltipData: { j },
      tooltipLeft: MARGIN.left + xScale(offsetCentre(offsetStartM, offsetStepM, j)),
      tooltipTop: MARGIN.top + 4,
    });
  };

  const onOverlayLeave = () => {
    setHoverJ(null);
    hideTooltip();
  };

  // ------------------------------------------------------------ copy
  // With a true section the subject is ONE chainage; with the bin means it is
  // the interval those means cover. Naming a range over a section plane — or a
  // single chainage over an interval mean — would misdescribe the drawing.
  const rangeText = line ? staOnly(line.staDisplayM) : bin ? formatStationRange(bin) : '';
  const areasText = line
    ? t('stationVolumes.section.areasAt', {
        // `valid: false` means no offset column had BOTH surfaces, so both
        // areas are structurally 0 rather than measured as 0 — an em dash,
        // never a zero (global constraint). The same holds for a stack, whose
        // station lines carry geometry but no end-area figures.
        cut: line.valid ? formatM2(line.cutAreaM2) : '—',
        fill: line.valid ? formatM2(line.fillAreaM2) : '—',
        station: staOnly(line.staDisplayM),
      })
    : t('stationVolumes.section.areas', {
        cut: formatM2(bin?.cutAreaM2 ?? 0),
        fill: formatM2(bin?.fillAreaM2 ?? 0),
      });
  // The row's Δz extremes are the BIN's per-cell min/max from the JSON, not
  // the min/max of the drawn columns: a column is a mean over its cells, so
  // reducing the means would quietly under-report the worst point in the
  // section. The footer says the drawing is column means; the readout says
  // what the bin actually contains.
  const dzText =
    bin && bin.minDzM != null && bin.maxDzM != null
      ? t('stationVolumes.section.dzMinMax', { min: formatFixed(bin.minDzM, 2), max: formatFixed(bin.maxDzM, 2) })
      : t('stationVolumes.section.dzUnknown');

  /**
   * The hidden data table carries what is drawn. A 200-column swath would be
   * 200 rows of screen-reader output, so wide sections are sampled and the
   * caption says by how much — the Table tab is the full-resolution view.
   */
  const srStride = Math.max(1, Math.ceil(nOffsets / SR_MAX_ROWS));
  const srRows = useMemo(() => {
    if (!row || nOffsets === 0) return [];
    const rows: { key: number; cells: string[] }[] = [];
    for (let j = 0; j < nOffsets; j += srStride) {
      const top = row.zTop[j];
      const bottom = row.zBottom[j];
      const diff = row.dz[j];
      rows.push({
        key: j,
        cells: [
          offsetTickLabel(offsetCentre(offsetStartM, offsetStepM, j)),
          Number.isFinite(top) ? formatFixed(top, 2) : '—',
          Number.isFinite(bottom) ? formatFixed(bottom, 2) : '—',
          Number.isFinite(diff) ? formatFixed(diff, 2) : '—',
        ],
      });
    }
    return rows;
  }, [row, nOffsets, srStride, offsetStartM, offsetStepM]);

  const hovered =
    hoverJ != null && row ? { top: row.zTop[hoverJ], bottom: row.zBottom[hoverJ], dz: row.dz[hoverJ] } : null;

  // While the pointer is over a column, the readout swaps from the BIN's
  // areas/Δz range to that column's own figures — a second, non-portalled
  // place the value shows up, so the reading survives even if the floating
  // tooltip is ever clipped or mispositioned by an ancestor. It reverts to
  // the bin figures the moment the pointer leaves (`hovered` goes null).
  //
  // No per-reference Δ suffix here any more (controller ruling, layout bug):
  // with several overlays configured, appending ` · <label> Δ +0.03` per
  // reference made this line wrap onto extra rows, which grew the control
  // row and shrank the ResizeObserver-measured plot area on every hover —
  // the chart visibly jumped. The per-reference rows already live in the
  // tooltip (`tipRows` below), Δ-to-reference stated there once. The readout
  // itself is now capped to one line (`truncate`, see the JSX) so this text
  // can never change the row's height again.
  const hoverReadoutText =
    hoverJ != null && hovered
      ? t(deviation ? 'stationVolumes.section.hoverReadoutDeviation' : 'stationVolumes.section.hoverReadout', {
          offset: offsetTickLabel(offsetCentre(offsetStartM, offsetStepM, hoverJ)),
          top: Number.isFinite(hovered.top) ? formatFixed(hovered.top, 2) : t('stationVolumes.section.noDataShort'),
          bottom: Number.isFinite(hovered.bottom)
            ? formatFixed(hovered.bottom, 2)
            : t('stationVolumes.section.noDataShort'),
          dz: Number.isFinite(hovered.dz) ? formatFixed(hovered.dz, 2) : t('stationVolumes.section.noDataShort'),
        })
      : null;

  const tipJ = tooltipData?.j ?? null;
  const tipRows =
    tipJ != null && row
      ? [
          // The unit is on the header line, once, not on all three rows.
          { label: topName, value: Number.isFinite(row.zTop[tipJ]) ? formatFixed(row.zTop[tipJ], 2) : '—' },
          { label: bottomName, value: Number.isFinite(row.zBottom[tipJ]) ? formatFixed(row.zBottom[tipJ], 2) : '—' },
          {
            label: t('stationVolumes.section.dz'),
            value: Number.isFinite(row.dz[tipJ])
              ? formatFixed(row.dz[tipJ], 2)
              : t('stationVolumes.section.noDataShort'),
          },
          // One row per REFERENCE that measured this column, after the run's
          // own three. The elevation answers "where is it", the Δ answers the
          // question a reference is actually put on the drawing for — how far
          // the as-built sits from it. No row at all for a column the
          // reference did not cover: an em dash there would read as a
          // measured zero.
          // One row per REFERENCE, kept SHORT: what the Δ is measured to is
          // stated once on the header line, not repeated per row.
          ...referenceRows.flatMap(({ series: s, row: r }) => {
            const z = r[tipJ];
            if (!Number.isFinite(z)) return [];
            const top = row.zTop[tipJ];
            const delta = Number.isFinite(top) ? ` (Δ ${signedDelta(z - top)})` : '';
            return [{ label: overlayNames[s.id] ?? s.label, value: `${formatFixed(z, 2)}${delta}` }];
          }),
        ]
      : [];

  // A3 / T13.3: the caption is about the ALIGNMENT's own profile, so it is
  // decided by the live registry alignment. `station_volumes.json` does not
  // echo the vertices, so when the alignment is not loaded there is nothing
  // to assert and the caption stays off rather than guessing.
  const registered = alignments.get(json.alignment.key);
  const flatAlignment = registered ? isFlat(registered.points) : false;

  const ticksAcross = Math.max(3, Math.floor(plotW / 70));
  const pxPerMetreX = plotW / offsetSpan;

  const veButton = (label: string, active: boolean, testId: string, onClick: () => void, title: string) => (
    <button
      key={testId}
      type='button'
      data-testid={testId}
      aria-pressed={active}
      title={title}
      onClick={onClick}
      className={`sv-num min-h-6 border-y border-r px-1.5 py-0.5 first:rounded-l first:border-l last:rounded-r focus-visible:relative focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:outline-none ${
        active ? 'border-current font-semibold' : 'border-slate-300 dark:border-stone-600'
      }`}
    >
      {label}
    </button>
  );

  const stepButton = (dir: -1 | 1, testId: string, label: string) => (
    <button
      type='button'
      data-testid={testId}
      aria-label={label}
      title={walkActive ? label : t('stationVolumes.section.walkHint')}
      aria-disabled={!walkActive}
      onClick={() => walkActive && stepSection(dir, false)}
      className={`flex h-6 min-w-6 items-center justify-center rounded border px-1 focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:outline-none ${
        walkActive ? 'border-slate-300 dark:border-stone-600' : 'border-slate-200 opacity-50 dark:border-stone-700'
      }`}
    >
      {dir === -1 ? '◀' : '▶'}
    </button>
  );

  const badgeLabels = {
    fetching: t('stationVolumes.section.fetching'),
    decoding: t('stationVolumes.section.decoding'),
    failed: t('stationVolumes.section.failed'),
  };

  // `offsets` OR true sections: the two are alternative sources for `row`,
  // and a run that has only the section planes (the multi-layer stack, which
  // writes no `station_offsets.f32`) draws from those. Before this, requiring
  // `offsets` here meant such a run computed a perfectly good `row` and then
  // refused to draw it.
  const drawn = (!!offsets || trueSections) && !!row && spans.valid;

  return (
    <div className='flex h-full min-h-0 w-full flex-col gap-1.5 text-slate-700 dark:text-stone-300'>
      <style>{SECTION_CSS}</style>

      {/* ---- Readout: which bin, what it measures, at what exaggeration ---- */}
      <div className='flex flex-wrap items-center gap-x-3 gap-y-1'>
        <div className='flex items-center gap-1.5'>
          {stepButton(-1, 'sv-section-prev', t('stationVolumes.section.prev'))}
          {stepButton(1, 'sv-section-next', t('stationVolumes.section.next'))}
          {/* An inert control with the reason hidden in a tooltip is a dead
              end; say out loud why the arrows do nothing. */}
          {!walkActive && (
            <span data-testid='sv-section-walk-hint' className='text-[11px] text-slate-500 dark:text-stone-400'>
              {t('stationVolumes.section.walkHint')}
            </span>
          )}
        </div>
        {/* No `key={binIndex}` here. Keying the readout on the bin remounted
            it — and replayed the 90 ms cross-fade — on every bin the walk
            cursor crossed, so scrubbing the alignment strobed instead of
            reading continuously (spec §4d asks for 60 fps). The cross-fade
            belongs to a change of SCOPE, not to a change of station. */}
        <div data-testid='sv-section-readout' className='flex flex-wrap items-center gap-x-2 gap-y-0.5'>
          <span
            className='sv-sta border-l-2 pl-1.5 font-semibold'
            style={{ borderColor: 'var(--sv-walk)' }}
            title={t('stationVolumes.panel.rangeLabel')}
          >
            {rangeText}
          </span>
          {/* Fixed one-line slot for the hover/idle readout (layout-bug fix).
              Hovering used to swap this to a line that grew with every
              configured reference overlay (` · <label> Δ +0.03` each), which
              wrapped the control row onto extra lines and shrank the
              ResizeObserver-measured plot area, so the chart jumped on every
              hover. The slot now has a fixed one-line `min-h` so idle ↔ hover
              can never change the row's height, `min-w-0` lets the flex child
              actually shrink instead of forcing the row wide, and `truncate`
              (Tailwind's `whitespace-nowrap overflow-hidden text-ellipsis`)
              clips instead of wrapping — the per-reference detail lives in the
              tooltip (`tipRows` below) instead of being crammed in here. */}
          <div className='flex min-h-[1.4em] min-w-0 flex-1 items-center gap-x-2 overflow-hidden'>
            {hoverReadoutText ? (
              <span
                className='sv-num min-w-0 truncate'
                data-testid='sv-section-readout-hover'
                title={hoverReadoutText}
              >
                {hoverReadoutText}
              </span>
            ) : (
              <>
                {/* Both figures are routinely misread: the areas are m² at a
                    plane (not the m³ the chart and table show), and the Δz range
                    covers the whole INTERVAL while the drawing is one plane
                    through it — so a reader cannot reproduce these extremes off
                    the drawn columns, and the tooltip says why. `truncate` can
                    now clip these in a narrow row, so `title` leads with the
                    VALUE (never lost, even truncated) and keeps the help
                    sentence after it — an em dash between them, same order the
                    reader would want it read aloud. */}
                <span
                  className='sv-num min-w-0 shrink truncate'
                  data-testid='sv-section-areas'
                  title={`${areasText} — ${t(trueSections ? 'stationVolumes.section.areasAtHelp' : 'stationVolumes.section.areasHelp')}`}
                >
                  {areasText}
                </span>
                <span
                  className='sv-num min-w-0 shrink truncate'
                  data-testid='sv-section-dz'
                  title={`${dzText} — ${t('stationVolumes.section.dzRangeHelp')}`}
                >
                  {dzText}
                </span>
              </>
            )}
          </div>
          {bin?.lowCoverage && (
            <span className='sv-chip sv-chip--warn sv-num' data-testid='sv-section-lowcov'>
              {t('stationVolumes.section.lowCoverage', { pct: formatPct(bin.coveragePct) })}
            </span>
          )}
        </div>

        {/* "Look at section" is about the STATION, not about the drawing: it
            frames the slab the clip box cut, which is worth doing even at a
            bin with no cells in it. It therefore sits outside the `drawn`
            guard — the clip hint in the footer names this button, and a
            sentence pointing at a control that is not on screen is worse than
            no sentence. The exaggeration control IS about the drawing, so it
            still appears only when there is one. */}
        {/* `flex-wrap`: the exaggeration ladder is nine stops wide now that it
            reaches 0.2× and 100×, which is more than a narrow dock fits beside
            the "Look at cross-section" button. Wrapping drops the group onto
            its own line rather than pushing the deep stops out of reach. */}
        <div className='ml-auto flex flex-wrap items-center justify-end gap-1.5'>
          <button
            type='button'
            data-testid='sv-look-at-section'
            aria-disabled={walkActive ? undefined : true}
            title={walkActive ? t('stationVolumes.section.lookAtHelp') : t('stationVolumes.section.walkHint')}
            onClick={lookAtSection}
            className={`min-h-6 rounded border px-2 text-[11px] focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:outline-none ${
              walkActive
                ? 'border-slate-300 hover:bg-slate-50 dark:border-stone-600 dark:hover:bg-stone-800'
                : 'border-slate-200 opacity-50 dark:border-stone-700'
            }`}
          >
            {t('stationVolumes.section.lookAt')}
          </button>
          {drawn && (
            <>
              <span className='text-[11px] text-slate-500 dark:text-stone-400'>{t('stationVolumes.section.ve')}</span>
              <div className='flex text-[11px]' role='group' aria-label={t('stationVolumes.section.veGroup')}>
                {veButton(
                  t('stationVolumes.section.veAuto', { factor: autoVe }),
                  veMode === 'auto',
                  'sv-ve-auto',
                  () => setVeMode('auto'),
                  t('stationVolumes.section.veAutoHelp'),
                )}
                {VE_PRESETS.map((p) =>
                  veButton(
                    t('stationVolumes.section.vePreset', { factor: p }),
                    veMode === p,
                    `sv-ve-${p}`,
                    () => setVeMode(p),
                    t('stationVolumes.section.veHelp', { factor: p }),
                  ),
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* ---- Legend: two named surfaces, two hatches, and holes ---- */}
      {drawn && (
        <div
          data-testid='sv-section-legend'
          className='flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-600 dark:text-stone-400'
        >
          {isStack ? (
            <>
              {/* One entry per LAYER, named from the level pair it lies
                  between — the legend of a stacked section is a materials
                  key, not a list of surfaces. A layer nobody has built yet
                  keeps its entry and says so: dropping it would leave the
                  reader counting bands to work out which one is missing. */}
              {layers.map((layer) => (
                <span
                  key={`legend-layer-${layer.i}`}
                  data-testid={`sv-legend-layer-${layer.i}`}
                  className={`flex items-center gap-1${layer.measured ? '' : ' opacity-60'}`}
                  title={t(
                    layer.measured
                      ? 'stationVolumes.section.legendLayerHelp'
                      : 'stationVolumes.section.legendLayerPendingHelp',
                    { from: levelLabels[layer.topLevel] ?? '', to: levelLabels[layer.bottomLevel] ?? '' },
                  )}
                >
                  <svg width={12} height={12} aria-hidden>
                    <rect x={0} y={0} width={12} height={12} fill='currentColor' fillOpacity={0.08} />
                    {layer.measured && (
                      <g transform={`scale(${12 / LAYER_HATCH_TILE})`}>{layerHatch(layer.i)('currentColor')}</g>
                    )}
                  </svg>
                  {t(
                    layer.measured ? 'stationVolumes.section.legendLayer' : 'stationVolumes.section.legendLayerPending',
                    { from: levelLabels[layer.topLevel] ?? '', to: levelLabels[layer.bottomLevel] ?? '' },
                  )}
                </span>
              ))}
              {designRows.length > 0 && (
                <span
                  data-testid='sv-legend-design'
                  className='flex items-center gap-1'
                  title={t('stationVolumes.section.legendDesignHelp')}
                >
                  <svg width={16} height={8} aria-hidden>
                    <line
                      x1={0}
                      y1={4}
                      x2={16}
                      y2={4}
                      stroke='var(--sv-surface-ink)'
                      strokeWidth={1.5}
                      strokeDasharray='2,3'
                      strokeOpacity={0.8}
                    />
                  </svg>
                  {t('stationVolumes.section.legendDesign')}
                </span>
              )}
            </>
          ) : (
            <>
              <span
                data-testid='sv-legend-top'
                className='flex items-center gap-1'
                title={topLayerName?.trim() || undefined}
              >
                <svg width={16} height={8} aria-hidden>
                  <line
                    className='sv-section-surface'
                    x1={0}
                    y1={4}
                    x2={16}
                    y2={4}
                    stroke='var(--sv-surface-ink)'
                    strokeWidth={2}
                  />
                </svg>
                {t('stationVolumes.section.legendTop', { name: topName })}
              </span>
              <span
                data-testid='sv-legend-bottom'
                className='flex items-center gap-1'
                title={bottomLayerName?.trim() || undefined}
              >
                <svg width={16} height={8} aria-hidden>
                  <line
                    className='sv-section-surface'
                    x1={0}
                    y1={4}
                    x2={16}
                    y2={4}
                    stroke='var(--sv-surface-ink)'
                    strokeWidth={2}
                    strokeDasharray='4,3'
                    strokeOpacity={0.75}
                  />
                </svg>
                {t('stationVolumes.section.legendBottom', { name: bottomName })}
              </span>
              <span
                data-testid='sv-legend-cut'
                className='flex items-center gap-1'
                title={t(
                  deviation ? 'stationVolumes.section.legendCutHelpDeviation' : 'stationVolumes.section.legendCutHelp',
                )}
              >
                <svg width={12} height={12} aria-hidden>
                  <rect x={0} y={0} width={12} height={12} fill='var(--sv-cut-wash)' />
                  <path d='M0,12 L12,0 M0,6 L6,0 M6,12 L12,6' stroke='var(--sv-cut)' strokeWidth={1.5} fill='none' />
                </svg>
                {/* The convention in words (T4): the `title` above explains
                    the GEOMETRY, this says which colour means which way round.
                    It already opens with the cut/fill word, so no separate
                    label sits beside it — that printed "Cut Cut · above
                    design". */}
                <span>{t(CUT_LEGEND_SHORT_KEY)}</span>
              </span>
              <span
                data-testid='sv-legend-fill'
                className='flex items-center gap-1'
                title={t(
                  deviation
                    ? 'stationVolumes.section.legendFillHelpDeviation'
                    : 'stationVolumes.section.legendFillHelp',
                )}
              >
                <svg width={12} height={12} aria-hidden>
                  <rect x={0} y={0} width={12} height={12} fill='var(--sv-fill-wash)' />
                  <path d='M0,0 L12,12 M0,6 L6,12 M6,0 L12,6' stroke='var(--sv-fill)' strokeWidth={1.5} fill='none' />
                </svg>
                <span>{t(FILL_LEGEND_SHORT_KEY)}</span>
              </span>
            </>
          )}
          {gaps.length > 0 && (
            <span
              data-testid='sv-legend-nodata'
              className='flex items-center gap-1'
              title={t('stationVolumes.section.legendNoDataHelp')}
            >
              <svg width={12} height={12} aria-hidden>
                <rect x={0} y={0} width={12} height={12} fill='currentColor' fillOpacity={0.08} />
                <path
                  d='M2,0 L2,12 M6,0 L6,12 M10,0 L10,12'
                  stroke='currentColor'
                  strokeWidth={1}
                  strokeOpacity={0.6}
                />
              </svg>
              {t('stationVolumes.section.legendNoData')}
            </span>
          )}
          {/* Out-of-tolerance legend entries: one per kind ACTUALLY drawn at
              this station, not one per kind the run's expectation could ever
              produce — an entry for a kind absent here would send the reader
              looking for a mark that is not on the drawing (same rule the
              no-data entry above already follows). */}
          {outKinds.has('under') && (
            <span
              data-testid='sv-legend-under'
              className='flex items-center gap-1'
              title={t('stationVolumes.section.legendUnderHelp')}
            >
              <svg width={12} height={12} aria-hidden>
                <rect x={0} y={0} width={12} height={12} fill='currentColor' fillOpacity={0.15} />
                <path d='M0,3 L12,3 M0,9 L12,9' stroke='currentColor' strokeWidth={1.5} />
              </svg>
              {t('stationVolumes.section.legendUnder')}
            </span>
          )}
          {outKinds.has('over') && (
            <span
              data-testid='sv-legend-over'
              className='flex items-center gap-1'
              title={t('stationVolumes.section.legendOverHelp')}
            >
              <svg width={12} height={12} aria-hidden>
                <rect x={0} y={0} width={12} height={12} fill='currentColor' fillOpacity={0.15} />
                <path d='M0,0 L12,12 M12,0 L0,12' stroke='currentColor' strokeWidth={1} />
              </svg>
              {t('stationVolumes.section.legendOver')}
            </span>
          )}
          {outKinds.has('crossing') && (
            <span
              data-testid='sv-legend-crossing'
              className='flex items-center gap-1'
              title={t('stationVolumes.section.legendCrossingHelp')}
            >
              <svg width={12} height={12} aria-hidden>
                <rect x={0} y={0} width={12} height={12} fill='currentColor' fillOpacity={0.2} />
                <circle cx={3} cy={3} r={1.4} fill='currentColor' />
                <circle cx={9} cy={3} r={1.4} fill='currentColor' />
                <circle cx={3} cy={9} r={1.4} fill='currentColor' />
                <circle cx={9} cy={9} r={1.4} fill='currentColor' />
              </svg>
              {t('stationVolumes.section.legendCrossing')}
            </span>
          )}
          {/* REFERENCE overlays: one entry per configured, non-hidden one, in
              the user's own order, whether or not it has a line on the
              drawing. A coloured 16 × 8 line swatch matches the stroke exactly
              — colour is the ONLY thing telling two references apart, since
              they share a weight and a geometry — and the name is always the
              file's own label, never the id.

              A pending or failed one is muted and `aria-disabled`: it must not
              read as data. It keeps its row because a reference that silently
              vanished would leave the reader unable to tell "not sampled yet"
              from "I never configured that". */}
          {overlayLegend.map((e) => {
            const reason = e.error ?? t('stationVolumes.section.overlays.failedUnknown');
            const failedCopy = t('stationVolumes.section.overlays.failed', { error: reason });
            return (
              <span
                key={`legend-ref-${e.id}`}
                data-testid={`sv-legend-ref-${e.id}`}
                data-state={e.state}
                className={`flex items-center gap-1${e.state === 'drawn' ? '' : ' opacity-60'}`}
                aria-disabled={e.state === 'drawn' ? undefined : true}
                title={
                  e.state === 'drawn'
                    ? `${e.label} — ${t('stationVolumes.section.overlays.legendHelp')}`
                    : e.state === 'failed'
                      ? failedCopy
                      : t('stationVolumes.section.overlays.pending')
                }
              >
                <svg width={16} height={8} aria-hidden>
                  <line
                    x1={0}
                    y1={4}
                    x2={16}
                    y2={4}
                    stroke={e.color}
                    strokeWidth={1.5}
                    strokeOpacity={e.state === 'drawn' ? 0.9 : 0.45}
                    strokeLinecap='round'
                    // A failed swatch is BROKEN as well as faint: pending and
                    // failed have to be told apart at a glance, and "muted"
                    // alone says only "not right now".
                    strokeDasharray={e.state === 'failed' ? '3,2' : undefined}
                  />
                </svg>
                {/* The one mark that survives without hovering. `aria-label`
                    carries the whole sentence, so a screen reader gets the
                    reason rather than a glyph name. */}
                {e.state === 'failed' && (
                  <span role='img' aria-label={failedCopy} className='text-[var(--sv-warn)] leading-none'>
                    ⚠
                  </span>
                )}
                {e.label}
                {/* Pending's own mark: an ellipsis says "still coming", which
                    a warning glyph would contradict. */}
                {e.state === 'pending' && <span aria-hidden>…</span>}
              </span>
            );
          })}
          {/* A SURFACES expectation on a file with NO design planes — every
              surface-difference run made before the multi-layer stack. There
              is nothing to compare each column against, so nothing is marked,
              and a silent absence is indistinguishable from "in tolerance
              everywhere" or "broken". Say which run it is, not that the
              feature is unavailable: on a stack file the very same expectation
              now draws its overlay from the design planes, and a note claiming
              otherwise while the marks are on screen would be worse than no
              note at all (surface-expectations §8.2a, resolved 2026-09-03). */}
          {surfacesWithoutDesign && (
            <span
              data-testid='sv-section-surfaces-note'
              className='flex items-center gap-1'
              title={t('stationVolumes.section.surfacesNoDesignHelp')}
            >
              {t('stationVolumes.section.surfacesNoDesign')}
            </span>
          )}
          {clipped && (
            <span className='sv-chip sv-chip--warn' data-testid='sv-section-clipped'>
              {t('stationVolumes.section.veClipped')}
            </span>
          )}
          <span className='ml-auto whitespace-nowrap'>{t('stationVolumes.panel.offsetConvention')}</span>
        </div>
      )}

      {/* ---- The pane: one tab stop, arrow-driven ---- */}
      <div
        data-testid='sv-section'
        className='sv-section flex min-h-0 w-full flex-1 flex-col outline-none'
        role='application'
        tabIndex={0}
        aria-label={t('stationVolumes.section.ariaSection', { station: rangeText, ve })}
        onKeyDown={onKeyDown}
      >
        <div ref={setPlotRef} className='relative min-h-0 w-full flex-1'>
          {!hasOffsetsByproduct && (
            <div className='flex flex-col items-start gap-1 py-4' data-testid='sv-section-missing'>
              <p className='text-sm font-semibold'>{t('stationVolumes.section.missingTitle')}</p>
              <p className='text-slate-500 dark:text-stone-400'>{t('stationVolumes.section.missingBody')}</p>
            </div>
          )}

          {hasOffsetsByproduct && error && (
            <div className='flex flex-col items-start gap-2 py-2' data-testid='sv-section-error'>
              <LoadingBadge name={json.alignment.name} phase='failed' error={error.message} labels={badgeLabels} />
              <p className='text-slate-500 dark:text-stone-400'>{t('stationVolumes.section.failedHelp')}</p>
              <button
                type='button'
                data-testid='sv-section-retry'
                onClick={refetch}
                className='min-h-6 rounded border border-slate-300 px-2 py-0.5 focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:outline-none dark:border-stone-600'
              >
                {t('stationVolumes.errors.retry')}
              </button>
            </div>
          )}

          {hasOffsetsByproduct && !error && !offsets && (
            <LoadingBadge
              name={json.alignment.name}
              phase={isLoading ? (progress?.phase ?? 'fetching') : 'fetching'}
              progress={progress?.totalBytes ? progress.loadedBytes / progress.totalBytes : undefined}
              loadedBytes={progress?.loadedBytes}
              totalBytes={progress?.totalBytes ?? undefined}
              labels={badgeLabels}
            />
          )}

          {/* Some overlay has no line on the drawing yet — the file does not
              carry its id (the server has not caught up), the file could not
              be read, or the file is not this run's grid. It sits where the
              section's own fetch badge sits, but floated over the drawing
              rather than in the flow: the section itself is complete and
              drawn, and only the reference lines are outstanding.

              The badge follows the STATES, not the settings hash: the planes
              the file already has are drawn throughout, and the badge speaks
              only for the ones that are not. */}
          {(overlaysFileFailed || overlaysPending) && (
            <div
              data-testid='sv-section-overlay-badge'
              data-phase={overlaysFileFailed ? 'failed' : 'decoding'}
              className='pointer-events-none absolute top-1 right-1 z-10 max-w-[60%]'
            >
              <LoadingBadge
                name={t(
                  overlaysFileFailed
                    ? 'stationVolumes.section.overlays.badgeFailed'
                    : 'stationVolumes.section.overlays.badge',
                )}
                phase={overlaysFileFailed ? 'failed' : 'decoding'}
                error={
                  overlaysError?.message ??
                  (overlaysGridMismatch ? t('stationVolumes.section.overlays.gridMismatch') : undefined)
                }
                labels={badgeLabels}
              />
            </div>
          )}

          {hasOffsetsByproduct && !error && offsets && !spans.valid && (
            <div className='flex flex-col items-start gap-2 py-4' data-testid='sv-section-empty'>
              <p className='text-sm font-semibold'>
                {t(deviation ? 'stationVolumes.section.noDataDeviation' : 'stationVolumes.section.noData')}
              </p>
              <p className='text-slate-500 dark:text-stone-400'>{t('stationVolumes.section.noDataBody')}</p>
              <button
                type='button'
                data-testid='sv-section-jump'
                onClick={jumpToData}
                aria-disabled={!walkActive}
                title={walkActive ? undefined : t('stationVolumes.section.walkHint')}
                className={`min-h-6 rounded border px-2 py-0.5 focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:outline-none ${
                  walkActive
                    ? 'border-slate-300 dark:border-stone-600'
                    : 'border-slate-200 opacity-50 dark:border-stone-700'
                }`}
              >
                {t('stationVolumes.section.jumpToData')}
              </button>
            </div>
          )}

          {drawn && (
            <svg
              data-testid='sv-section-svg'
              width={width}
              height={height}
              role='img'
              aria-label={t('stationVolumes.section.ariaSummary', {
                station: rangeText,
                top: topName,
                bottom: bottomName,
                ve,
              })}
              data-ve={ve}
              data-px-per-metre-x={pxPerMetreX}
              data-px-per-metre-z={pixelsPerMetreZ(ve, offsetSpan, plotW)}
              data-plot-width={plotW}
            >
              <defs>
                {/* 45° for cut, 135° for fill — the same polarity the bar
                    chart uses, so the two views read as one instrument. */}
                <pattern
                  id={`${uid}-cut`}
                  width={6}
                  height={6}
                  patternTransform='rotate(45)'
                  patternUnits='userSpaceOnUse'
                >
                  {/* Centred in the 6-wide tile: at x = 0 half of a 1.5 px
                      stroke falls outside the tile and the hatch draws at
                      half weight against the full-weight legend swatch. */}
                  <line x1={3} y1={0} x2={3} y2={6} stroke='var(--sv-cut)' strokeWidth={1.5} />
                </pattern>
                <pattern
                  id={`${uid}-fill`}
                  width={6}
                  height={6}
                  patternTransform='rotate(135)'
                  patternUnits='userSpaceOnUse'
                >
                  <line x1={3} y1={0} x2={3} y2={6} stroke='var(--sv-fill)' strokeWidth={1.5} />
                </pattern>
                {/* Holes get VERTICAL stripes, deliberately not a 45°/135°
                    hatch: "we did not measure here" must not look like a
                    faint cut. */}
                <pattern id={`${uid}-nodata`} width={4} height={4} patternUnits='userSpaceOnUse'>
                  <line x1={2} y1={0} x2={2} y2={4} stroke='currentColor' strokeWidth={1} strokeOpacity={0.45} />
                </pattern>
                {/* Out-of-tolerance overlays (spec §8.2). Three DISTINCT
                    motifs, not three shades of one hatch — a greyscale print
                    still has to tell "too thin" from "too thick" from
                    "crossing" apart, and every angle already in use (45° cut,
                    135° fill, vertical no-data) is spoken for, so these are
                    horizontal, a grid and dots. All draw in `currentColor` for
                    the same reason: identity here is the PATTERN, never a hue. */}
                <pattern id={`${uid}-under`} width={6} height={6} patternUnits='userSpaceOnUse'>
                  <line x1={0} y1={3} x2={6} y2={3} stroke='currentColor' strokeWidth={1.5} />
                </pattern>
                <pattern id={`${uid}-over`} width={6} height={6} patternUnits='userSpaceOnUse'>
                  <line x1={0} y1={0} x2={6} y2={6} stroke='currentColor' strokeWidth={1} />
                  <line x1={6} y1={0} x2={0} y2={6} stroke='currentColor' strokeWidth={1} />
                </pattern>
                <pattern id={`${uid}-crossing`} width={6} height={6} patternUnits='userSpaceOnUse'>
                  <circle cx={3} cy={3} r={1.4} fill='currentColor' />
                </pattern>
                {/* One pattern per LAYER of a stack. Same ink, same weight,
                    different geometry — see `LAYER_HATCHES`. */}
                {isStack &&
                  LAYER_HATCHES.map((paint, h) => (
                    <pattern
                      key={`layer-${h}`}
                      id={`${uid}-layer${h}`}
                      width={LAYER_HATCH_TILE}
                      height={LAYER_HATCH_TILE}
                      patternUnits='userSpaceOnUse'
                    >
                      {paint('currentColor')}
                    </pattern>
                  ))}
                {/* A layer's out-of-tolerance overlay is clipped to the layer
                    itself. Full-height overlays are right while there is one
                    band — the reader has to see WHERE along the section it
                    breaks — but N−1 of them stacked over each other are mud,
                    and they would also point at the wrong layer. */}
                {isStack &&
                  layers
                    .filter((l) => l.measured)
                    .map((l) => (
                      <clipPath key={`layerclip-${l.i}`} id={`${uid}-layerclip${l.i}`}>
                        {l.bands.map((band, bi) => (
                          <polygon key={bi} points={polygonPoints(band.points, xScale, yScale)} />
                        ))}
                      </clipPath>
                    ))}
                <clipPath id={`${uid}-clip`}>
                  <rect x={0} y={0} width={plotW} height={plotH} />
                </clipPath>
              </defs>

              <Group left={MARGIN.left} top={MARGIN.top}>
                {/* Hairline grid, one step off surface, drawn under the data. */}
                {yScale.ticks(4).map((tick) => (
                  <line
                    key={`gy-${tick}`}
                    x1={0}
                    x2={plotW}
                    y1={yScale(tick)}
                    y2={yScale(tick)}
                    stroke='var(--sv-grid)'
                  />
                ))}
                {xScale.ticks(ticksAcross).map((tick) => (
                  <line
                    key={`gx-${tick}`}
                    x1={xScale(tick)}
                    x2={xScale(tick)}
                    y1={0}
                    y2={plotH}
                    stroke='var(--sv-grid)'
                  />
                ))}

                <g clipPath={`url(#${uid}-clip)`}>
                  {/* Holes first: they are the ground the rest is drawn on. */}
                  {gaps.map((g) => {
                    const x0 = xScale(offsetStartM + g.from * offsetStepM);
                    const x1 = xScale(offsetStartM + (g.to + 1) * offsetStepM);
                    return (
                      <rect
                        key={`gap-${g.from}`}
                        data-testid='sv-section-nodata'
                        x={Math.min(x0, x1)}
                        y={0}
                        width={Math.abs(x1 - x0)}
                        height={plotH}
                        fill={`url(#${uid}-nodata)`}
                      >
                        <title>{t('stationVolumes.section.legendNoDataHelp')}</title>
                      </rect>
                    );
                  })}

                  {/* One group per LAYER. A layer nobody has built yet gets
                      no group at all: `PENDING` is a state, and a band drawn
                      for it would read as "measured, and it came out at
                      0 mm" (global constraint: absence is never a zero). */}
                  {layers.map((layer) =>
                    layer.measured ? (
                      <g
                        key={`layer-${layer.i}`}
                        data-testid={`sv-section-band-${layer.i}`}
                        data-layer={layer.i}
                        data-active={isStack && layer.i === Math.min(...activePair) ? 'true' : undefined}
                        // The layer's own hatch, inherited by the polygons
                        // below. On a single-pair run there is no layer
                        // identity to carry and the cut/fill hatch stands.
                        fill={isStack ? `url(#${uid}-layer${layer.i % LAYER_HATCHES.length})` : undefined}
                      >
                        {layer.bands.map((band, bi) => {
                          const pts = polygonPoints(band.points, xScale, yScale);
                          const cut = band.kind === 'cut';
                          return (
                            <g key={`band-${bi}`} data-testid='sv-section-band' data-kind={band.kind}>
                              <polygon
                                className='sv-section-wash'
                                points={pts}
                                fill={cut ? 'var(--sv-cut-wash)' : 'var(--sv-fill-wash)'}
                              />
                              <polygon points={pts} fill={isStack ? undefined : `url(#${uid}-${band.kind})`} />
                            </g>
                          );
                        })}
                        {/* This layer's OWN verdict, clipped to this layer. */}
                        {isStack &&
                          layer.out.map((r, ri) => {
                            const x0 = xScale(offsetStartM + r.from * offsetStepM);
                            const x1 = xScale(offsetStartM + (r.to + 1) * offsetStepM);
                            return (
                              <rect
                                key={`out-${r.kind}-${ri}`}
                                data-testid={`sv-section-out-${r.kind}`}
                                data-kind={r.kind}
                                data-layer={layer.i}
                                clipPath={`url(#${uid}-layerclip${layer.i})`}
                                x={Math.min(x0, x1)}
                                y={0}
                                width={Math.abs(x1 - x0)}
                                height={plotH}
                                fill={`url(#${uid}-${r.kind})`}
                                fillOpacity={r.kind === 'crossing' ? 0.75 : 0.6}
                              >
                                <title>
                                  {t(
                                    r.kind === 'under'
                                      ? 'stationVolumes.section.legendUnderHelp'
                                      : r.kind === 'over'
                                        ? 'stationVolumes.section.legendOverHelp'
                                        : 'stationVolumes.section.legendCrossingHelp',
                                  )}
                                </title>
                              </rect>
                            );
                          })}
                      </g>
                    ) : null,
                  )}

                  {/* Out-of-tolerance overlays, drawn full-height across the
                      run exactly like `gaps` above — the reader has to see
                      WHERE along the section a layer breaks its expectation,
                      not just that the corridor's summary share is nonzero.
                      On top of the cut/fill bands (which they annotate) and
                      below the two surface lines (which stay the crisp,
                      readable measurement on top). */}
                  {outRuns.map((r, i) => {
                    const x0 = xScale(offsetStartM + r.from * offsetStepM);
                    const x1 = xScale(offsetStartM + (r.to + 1) * offsetStepM);
                    return (
                      <rect
                        key={`out-${r.kind}-${i}`}
                        data-testid={`sv-section-out-${r.kind}`}
                        data-kind={r.kind}
                        x={Math.min(x0, x1)}
                        y={0}
                        width={Math.abs(x1 - x0)}
                        height={plotH}
                        fill={`url(#${uid}-${r.kind})`}
                        fillOpacity={r.kind === 'crossing' ? 0.55 : 0.4}
                      >
                        <title>
                          {t(
                            r.kind === 'under'
                              ? 'stationVolumes.section.legendUnderHelp'
                              : r.kind === 'over'
                                ? 'stationVolumes.section.legendOverHelp'
                                : 'stationVolumes.section.legendCrossingHelp',
                          )}
                        </title>
                      </rect>
                    );
                  })}

                  {/* The DESIGN levels, under the as-built ones: a reference,
                      never a measurement. Dashed and light, so nothing on the
                      drawing can be mistaken for a surveyed surface. Absent on
                      every run that carries no design planes. */}
                  {designRows.map((plane, di) => {
                    const d = polylinePath(plane, validRuns(plane), offsetStartM, offsetStepM, xScale, yScale);
                    return d ? (
                      <path
                        key={`design-${di}`}
                        data-testid={`sv-section-design-${di}`}
                        className='sv-section-surface'
                        d={d}
                        fill='none'
                        stroke='var(--sv-surface-ink)'
                        strokeOpacity={0.55}
                        strokeWidth={1.25}
                        strokeDasharray='2,3'
                        strokeLinecap='round'
                      >
                        <title>{t('stationVolumes.section.legendDesign')}</title>
                      </path>
                    ) : null;
                  })}

                  {/* The REFERENCE overlays, over the bands and UNDER the
                      as-built lines: a reference is context for the survey,
                      never a thing that hides it. Each carries its own ink and
                      a thinner weight than the surveyed levels, so the drawing
                      answers "what did we measure" before "what is it near".
                      A plane the sampler could not produce is all NaN and
                      yields no path at all — the legend says why. */}
                  {referenceRows.map(({ series: s, row: r }, ri) => {
                    const d = polylinePath(r, validRuns(r), offsetStartM, offsetStepM, xScale, yScale);
                    return d ? (
                      <path
                        key={`ref-${s.id}`}
                        data-testid={`sv-section-ref-${s.id}`}
                        // The second class is inert in normal colours and is
                        // the line's whole identity under forced ones — see
                        // the dash rules in `SECTION_CSS`.
                        className={`sv-section-surface sv-section-ref-${ri % 8}`}
                        d={d}
                        fill='none'
                        stroke={s.color}
                        strokeOpacity={0.9}
                        strokeWidth={1.25}
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      ></path>
                    ) : null;
                  })}

                  {/* The AS-BUILT levels. Neutral ink, not the cut/fill hues:
                      the hue belongs to the material between them, and a red
                      "survey" line would claim the survey is the cut.
                      Painted bottom level first, so the upper surfaces sit on
                      top where two of them meet — the paint order the shipped
                      pair already had. A level nobody surveyed has no path at
                      all rather than an empty one. */}
                  {levelRows
                    .map((plane, li) => {
                      const d = polylinePath(plane, validRuns(plane), offsetStartM, offsetStepM, xScale, yScale);
                      if (!d) return null;
                      const role = li === activePair[0] ? 'top' : li === activePair[1] ? 'bottom' : undefined;
                      // Single pair: the shipped styling — the lower surface
                      // dashed, so the two are told apart by pattern and not
                      // by ink (it survives forced colours). A stack dashes
                      // nothing: every as-built level is equally measured, and
                      // the dash is spoken for by the design lines.
                      const dashed = !isStack && li === 1;
                      const emphasised = !isStack || role !== undefined;
                      return (
                        <path
                          key={`line-${li}`}
                          data-testid={`sv-section-line-${li}`}
                          data-role={role}
                          className='sv-section-surface'
                          d={d}
                          fill='none'
                          stroke='var(--sv-surface-ink)'
                          strokeOpacity={emphasised ? (dashed ? 0.75 : 1) : 0.5}
                          strokeWidth={emphasised ? 2 : 1.25}
                          strokeDasharray={dashed ? '5,3' : undefined}
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        >
                          {isStack && levelLabels[li] ? <title>{levelLabels[li]}</title> : null}
                        </path>
                      );
                    })
                    .reverse()}

                  {/* Crossfall (crossfall spec §6): the two fitted lines and
                      one label per band, from the endpoints the worker
                      computed. `line` is this station line's record; a run
                      with no check has no `crossfall` key at all. */}
                  {line?.crossfall && (
                    <SectionCrossfallLabels crossfall={line.crossfall} xScale={xScale} yScale={yScale} plotH={plotH} />
                  )}

                  {hoverJ != null && hovered && (
                    <g className='sv-section-crosshair' data-testid='sv-section-crosshair'>
                      <line
                        x1={xScale(offsetCentre(offsetStartM, offsetStepM, hoverJ))}
                        x2={xScale(offsetCentre(offsetStartM, offsetStepM, hoverJ))}
                        y1={0}
                        y2={plotH}
                        stroke='currentColor'
                        strokeOpacity={0.45}
                        strokeDasharray='2,2'
                      />
                      {[hovered.top, hovered.bottom].map((z, i) =>
                        Number.isFinite(z) ? (
                          <circle
                            key={i}
                            cx={xScale(offsetCentre(offsetStartM, offsetStepM, hoverJ))}
                            cy={yScale(z)}
                            r={4}
                            fill='var(--sv-surface-ink)'
                            stroke='var(--sv-surface)'
                            strokeWidth={2}
                          />
                        ) : null,
                      )}
                    </g>
                  )}
                </g>

                {/* Swath limits: the analysis stops here, and a reader must
                    not extrapolate the surfaces past them. Drawn at the
                    requested swath, not at the pane edge — the offset bin
                    step is widened to the grid resolution, so the last
                    column can reach a little past the swath. */}
                {[-json.params.swathLeftM, json.params.swathRightM].map((o) => (
                  <line
                    key={`swath-${o}`}
                    x1={clamp(xScale(o), 0.5, plotW - 0.5)}
                    x2={clamp(xScale(o), 0.5, plotW - 0.5)}
                    y1={0}
                    y2={plotH}
                    stroke='currentColor'
                    strokeOpacity={0.4}
                    strokeDasharray='3,3'
                  />
                ))}

                {/* Centreline tick, in the walk cursor's amber: this section
                    IS the walked station, and the same colour object marks
                    it in the chart and in 3D. */}
                <line
                  data-testid='sv-section-centreline'
                  x1={xScale(0)}
                  x2={xScale(0)}
                  y1={plotH - 8}
                  y2={plotH}
                  stroke='var(--sv-walk)'
                  strokeWidth={2}
                />

                <AxisLeft
                  scale={yScale}
                  numTicks={4}
                  stroke='currentColor'
                  tickStroke='currentColor'
                  tickFormat={(v) => formatFixed(Number(v), 2)}
                  tickLabelProps={tickLabelProps({
                    textAnchor: 'end',
                    dx: '-0.25em',
                    dy: '0.25em',
                    className: 'sv-num',
                  })}
                />
                <text
                  data-testid='sv-section-y-axis-label'
                  x={-MARGIN.left + 2}
                  y={-3}
                  fontSize={TICK_FONT}
                  fill='currentColor'
                  opacity={0.7}
                >
                  {t('stationVolumes.section.elevationAxis', { ve })}
                </text>

                <AxisBottom
                  scale={xScale}
                  top={plotH}
                  numTicks={ticksAcross}
                  stroke='currentColor'
                  tickStroke='currentColor'
                  tickFormat={(v) => offsetTickLabel(Number(v))}
                  tickLabelProps={tickLabelProps({ textAnchor: 'middle', className: 'sv-num' })}
                />
                {/* Side labels at the ends of the axis: L and R are the whole
                    convention, and they belong where the reader's eye lands. */}
                <text x={0} y={plotH + 28} fontSize={TICK_FONT} fill='currentColor' opacity={0.7} textAnchor='start'>
                  {t('stationVolumes.section.left')}
                </text>
                <text x={plotW} y={plotH + 28} fontSize={TICK_FONT} fill='currentColor' opacity={0.7} textAnchor='end'>
                  {t('stationVolumes.section.right')}
                </text>

                {/* Hit overlay: full pane, so every column has a ≥ 24 px
                    target, and drawn LAST so it sits above the hatched bands
                    and surface lines in paint order and catches the pointer
                    before them. `fill='transparent'` (not `'none'`) keeps it
                    hit-testable under the default `visiblePainted`; the
                    explicit `pointerEvents: 'all'` is a belt-and-braces
                    guard against an ancestor style turning events off. */}
                <rect
                  data-testid='sv-section-overlay'
                  x={0}
                  y={0}
                  width={plotW}
                  height={plotH}
                  fill='transparent'
                  style={{ cursor: 'crosshair', pointerEvents: 'all' }}
                  onPointerMove={onOverlayMove}
                  onPointerLeave={onOverlayLeave}
                />
              </Group>
            </svg>
          )}

          {tooltipOpen && tipRows.length > 0 && tipJ != null && (
            <TooltipInPortal
              top={tooltipTop}
              left={tooltipLeft}
              style={FLOATING_TOOLTIP_STYLE}
              className='pointer-events-none !rounded-md !bg-slate-800 !px-2 !py-1.5 !text-xs !text-white !shadow-lg'
            >
              <div data-testid='sv-section-tooltip'>
                <TooltipCard
                  station={
                    // The Δ's reference is stated ONCE, here, so every
                    // reference row below can stay a name and two figures.
                    t('stationVolumes.section.offsetAt', {
                      offset: offsetTickLabel(offsetCentre(offsetStartM, offsetStepM, tipJ)),
                    }) +
                    (referenceRows.length > 0
                      ? ` · ${t('stationVolumes.section.overlays.deltaVs', { name: topName })}`
                      : '')
                  }
                  rows={tipRows}
                />
              </div>
            </TooltipInPortal>
          )}
        </div>
      </div>

      {/* The same visually-hidden table the bar chart uses (spec §5 A11y
          point 4) — one implementation, so the two views cannot drift — and,
          like the chart's, a SIBLING of the `role="application"` pane rather
          than a descendant. Inside it, assistive tech stops exposing the rows
          as a table at all, which is the whole point of having one. */}
      {drawn && (
        <HiddenDataTable
          // The visible legend and the `aria-label` summary already name the
          // run's two inputs; this caption is the only description a screen
          // reader gets of the SAME drawing, so it has to name them the same
          // way — and, on a deviation run, say which sign of the Δz column is
          // cut (`dz = top − bottom` = −deviation, so a negative Δz is
          // material above the design). The visible reader has that on the
          // dock header's `sv-cut-convention` line.
          caption={
            srStride > 1
              ? t(
                  deviation
                    ? 'stationVolumes.section.srCaptionSampledDeviation'
                    : 'stationVolumes.section.srCaptionSampled',
                  { station: rangeText, n: srStride },
                )
              : t(deviation ? 'stationVolumes.section.srCaptionDeviation' : 'stationVolumes.section.srCaption', {
                  station: rangeText,
                })
          }
          headers={[t('stationVolumes.section.colOffset'), topName, bottomName, t('stationVolumes.section.dz')]}
          rows={srRows}
          currentRow={null}
        />
      )}

      {/* ---- Footer: where these elevations come from ---- */}
      <div className='flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-slate-500 dark:text-stone-400'>
        <span>
          {trueSections
            ? t('stationVolumes.section.sectionNote', { step: metres(offsetStepM) })
            : t('stationVolumes.section.meanNote', { length: Math.round(json.params.stationIntervalM) })}
        </span>
        {flatAlignment && (
          <span data-testid='sv-section-flat'>
            {t(deviation ? 'stationVolumes.section.flatCaptionDeviation' : 'stationVolumes.section.flatCaption')}
          </span>
        )}
        {/* What the clip box is doing to the scene, said where the drawing it
            matches is (spec §11.4). Only while it is actually on.
            ONE sentence, and it ends by naming the control that acts on it:
            the hint used to state the slab's dimensions and stop, leaving the
            "Look at section" button above to make its own separate offer, and
            a reader had to join the two up themselves. `lookAt` is
            interpolated from the button's own label, so the sentence keeps
            pointing at the visible control in every locale.

            `walkActive` as well as the toggle: `stationClipToBinAtom` survives
            the end of a walk (it is only reset when the dock closes, ruling
            D36) while the dock's clip-follow effect is gated on the walk, so
            the toggle can sit ticked over a scene that is not clipped — and
            the sentence would then also point at a button that is inert. Say
            it only while it is true. */}
        {clipToBin && walkActive && (
          <span data-testid='sv-section-clip-hint'>
            {t('stationVolumes.section.clipHint', {
              length: metres(json.params.stationIntervalM),
              width: metres(json.params.swathLeftM + json.params.swathRightM),
              lookAt: t('stationVolumes.section.lookAt'),
            })}
          </span>
        )}
      </div>
    </div>
  );
};
